"use client";

// Figma 8218:20401 — feature-grid card art (371×371). Live HTML product UI mocks.

import { Check } from "iconoir-react";
import { useLocale } from "next-intl";
import Image from "next/image";

import { formatLocalizedCurrency } from "@/lib/localize-currency";

import { BrowserDots, MockFrame } from "./shared";

const SUPPORT_TEAM_CARD =
  "/assets-next/wholesale-financing/features-grid/support-team-card.png";

/** Card 1 — business funding selector (8218:20407). Mirrors Home GuaranteesCard. */
export function BusinessFundingMock() {
  return (
    <MockFrame size="square" className="border-0 bg-[#f9f9f8]">
      {/* Faded "No personal liability" row (Figma 8218:22285). */}
      <div className="bg-grey-50 absolute top-[60%] left-[13%] h-[24%] w-[142%] overflow-hidden rounded-[13px] opacity-44">
        <span className="bg-grey-0 absolute top-[11%] left-[2%] flex size-[18%] min-h-[48px] min-w-[48px] items-center justify-center rounded shadow-[-12px_10px_36px_rgba(0,0,0,0.05)]">
          <span className="bg-grey-100 flex size-5 items-center justify-center rounded-full">
            <Check className="text-grey-0 size-3" strokeWidth={3} aria-hidden />
          </span>
        </span>
        <span className="text-grey-950 absolute top-[38%] left-[19%] text-[13px] leading-[1.4] tracking-[-0.02em] opacity-30">
          No personal liability
        </span>
      </div>

      {/* Gray glow (Figma 8218:22290) — soft drop shadow under the glass bar. */}
      <span
        aria-hidden
        className="pointer-events-none absolute top-[53%] right-[-10%] bottom-0 left-[8%] rounded-2xl blur-[8px]"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.06) 42%, rgba(0,0,0,0) 100%)",
        }}
      />

      {/* Blue glow (Figma 8218:22291) — flipped so intensity sits around the check. */}
      <span
        aria-hidden
        className="pointer-events-none absolute top-[29%] left-[9%] h-[24%] w-[65%] -scale-x-100"
      >
        <span className="absolute inset-[-44.9%_-16.6%]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/home/guarantee-glow-blue.svg"
            alt=""
            className="block size-full max-w-none"
          />
        </span>
      </span>

      {/* Frosted glass UI (Figma 8218:22293). */}
      <div
        className="absolute top-[27%] left-[9%] flex h-[29%] w-[155%] items-center rounded-3xl border border-white/20 px-3 backdrop-blur-[9px]"
        style={{
          backgroundImage:
            "linear-gradient(114deg, rgba(255,255,255,0.05) 22%, rgba(255,255,255,0.1) 82%)",
        }}
      >
        <div className="bg-grey-0 border-grey-100 flex h-[76%] w-full items-center gap-4 overflow-hidden rounded-xl border pl-2">
          <span className="bg-grey-0 flex size-[17%] min-h-[52px] min-w-[52px] shrink-0 items-center justify-center rounded shadow-[0_9px_33px_rgba(0,0,0,0.16)]">
            <span className="bg-pulse-700 flex size-6 items-center justify-center rounded-full">
              <Check
                className="text-grey-0 size-3.5"
                strokeWidth={3}
                aria-hidden
              />
            </span>
          </span>
          <span className="text-grey-500 text-[18px] leading-[1.4] tracking-[-0.02em] whitespace-nowrap">
            Business funding
          </span>
        </div>
      </div>
    </MockFrame>
  );
}

/** Card 2 — support team gradient + avatars (8218:20537). */
export function SupportTeamMock() {
  return (
    <MockFrame size="square" className="bg-[#f9f9f8] p-0">
      <Image
        src={SUPPORT_TEAM_CARD}
        alt=""
        fill
        sizes="371px"
        className="rounded-2xl object-cover"
      />
    </MockFrame>
  );
}

/** Card 3 — financing summary browser mock (8218:20512). */
export function FinancingSummaryMock() {
  const locale = useLocale();
  const rows = [
    {
      label: "Financing amount",
      value: formatLocalizedCurrency(100_000, locale),
    },
    { label: "Fee", value: formatLocalizedCurrency(4_200, locale) },
  ];

  return (
    <MockFrame size="square" className="bg-grey-50">
      <div className="bg-grey-0 absolute top-1/2 left-[58%] w-[101%] max-w-[375px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-3xl p-[18px] shadow-[-16px_0_48px_rgba(0,0,0,0.07)]">
        <div className="flex flex-col gap-[18px]">
          <BrowserDots />
          <div className="bg-grey-50 rounded-xl p-[30px]">
            <dl className="flex flex-col">
              {rows.map(({ label, value }) => (
                <div
                  key={label}
                  className="border-grey-100 flex items-center justify-between border-b py-3"
                >
                  <dt className="text-grey-600 text-[14px] leading-[1.4] tracking-[-0.02em]">
                    {label}
                  </dt>
                  <dd className="text-grey-600 text-[21px] leading-[1.4] tracking-[-0.02em]">
                    {value}
                  </dd>
                </div>
              ))}
              <div className="flex items-center justify-between pt-4">
                <dt className="text-grey-600 text-[14px] leading-[1.4] tracking-[-0.02em]">
                  Total to repay
                </dt>
                <dd className="text-grey-950 text-[26px] leading-[1.2] tracking-[-0.04em]">
                  {formatLocalizedCurrency(104_200, locale)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </MockFrame>
  );
}

export type FeaturesGridMockVariant =
  | "businessFunding"
  | "supportTeam"
  | "financingSummary";

export function FeaturesGridMock({
  variant,
}: {
  variant: FeaturesGridMockVariant;
}) {
  switch (variant) {
    case "businessFunding":
      return <BusinessFundingMock />;
    case "supportTeam":
      return <SupportTeamMock />;
    case "financingSummary":
      return <FinancingSummaryMock />;
  }
}
